/**
 * CapabilityToolkits - Organized toolkits for Content Manager capabilities
 *
 * Presents all available tools organized by purpose (not sequence).
 * Content Managers can jump to any toolkit based on their current needs.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  TrendingUp,
  Sparkles,
  Search,
  PenTool,
  CheckSquare,
  Download,
  BarChart3,
  Compass,
  BookOpen,
  HelpCircle,
  Target,
  FileText,
  MessageSquare,
  Users,
  Package,
  AlertTriangle,
  Lightbulb,
  Trophy,
  Layers,
  Video,
  Newspaper,
  Building2,
  Database,
  Zap,
  ArrowRight
} from 'lucide-react';

interface ToolkitAction {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: React.ElementType;
  color: string;
  badge?: string;
  badgeColor?: string;
  isNew?: boolean;
  comingSoon?: boolean;
}

interface Toolkit {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  actions: ToolkitAction[];
}

const toolkits: Toolkit[] = [
  {
    id: 'market-intelligence',
    name: 'Market Intelligence',
    description: 'Understand your market, customers, and opportunities',
    icon: Compass,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    actions: [
      {
        id: 'latest-trends',
        label: 'Latest Trends',
        description: 'See what\'s trending in your sector',
        path: '/about-business/sector?tab=trends',
        icon: TrendingUp,
        color: 'text-blue-500'
      },
      {
        id: 'category-wheel',
        label: 'Category Demand',
        description: 'Which product categories have more traction',
        path: '/about-business/sector?tab=categories',
        icon: BarChart3,
        color: 'text-indigo-500'
      },
      {
        id: 'research-topics',
        label: 'Technical Terms',
        description: 'Learn sector-specific terminology',
        path: '/about-business/sector?tab=research',
        icon: BookOpen,
        color: 'text-purple-500'
      },
      {
        id: 'question-wheels',
        label: 'Customer Questions',
        description: 'See what customers are asking',
        path: '/about-business/sector?tab=questions',
        icon: HelpCircle,
        color: 'text-cyan-500'
      },
      {
        id: 'buyer-personas',
        label: 'Buyer Personas',
        description: 'Understand your target customers',
        path: '/about-business/sector?tab=personas',
        icon: Users,
        color: 'text-pink-500'
      }
    ]
  },
  {
    id: 'data-quality',
    name: 'Data Quality',
    description: 'Clean and enrich your product catalog',
    icon: Sparkles,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    actions: [
      {
        id: 'ai-enrich',
        label: 'AI Enrich Products',
        description: 'Extract hidden gems from product descriptions',
        path: '/about-business/offering?tab=products',
        icon: Sparkles,
        color: 'text-purple-500'
      },
      {
        id: 'review-enrichments',
        label: 'Review Enrichments',
        description: 'Spot data issues, typos, and gaps',
        path: '/research/enrichment-review',
        icon: AlertTriangle,
        color: 'text-amber-500'
      },
      {
        id: 'data-sources',
        label: 'Manage Data Sources',
        description: 'Upload and manage product feeds',
        path: '/research/data-sources',
        icon: Database,
        color: 'text-slate-500'
      }
    ]
  },
  {
    id: 'research-strategy',
    name: 'Research & Strategy',
    description: 'Find high-value opportunities and build knowledge',
    icon: Search,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    actions: [
      {
        id: 'evaluate-questions',
        label: 'Evaluate Questions',
        description: 'Find valuable questions by search volume',
        path: '/research/question-wheel',
        icon: Target,
        color: 'text-orange-500'
      },
      {
        id: 'my-topics',
        label: 'Research Topics',
        description: 'Ask PageMind to investigate new topics',
        path: '/my-topics',
        icon: Lightbulb,
        color: 'text-yellow-500'
      },
      {
        id: 'question-recommendations',
        label: 'Question Recommendations',
        description: 'AI recommendations on who should answer what',
        path: '/about-business/sector?tab=questions',
        icon: MessageSquare,
        color: 'text-teal-500'
      },
      {
        id: 'knowledge-base',
        label: 'My Knowledge Base',
        description: 'Add your own documents for Q&A',
        path: '/my-knowledge-base',
        icon: Database,
        color: 'text-blue-500',
        comingSoon: true
      }
    ]
  },
  {
    id: 'content-creation',
    name: 'Content Creation',
    description: 'Generate AEO-optimized content at scale',
    icon: PenTool,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    actions: [
      {
        id: 'expert-interviews',
        label: 'Expert Interviews',
        description: 'Create interviews with product experts',
        path: '/admin/experts',
        icon: Video,
        color: 'text-red-500'
      },
      {
        id: 'ai-articles-personas',
        label: 'Articles for Personas',
        description: 'Write articles for specific buyer personas',
        path: '/about-business/sector?tab=personas',
        icon: Users,
        color: 'text-pink-500'
      },
      {
        id: 'ai-articles-trends',
        label: 'Trend Articles',
        description: 'Write about latest trends with your products',
        path: '/about-business/sector?tab=trends',
        icon: TrendingUp,
        color: 'text-blue-500'
      },
      {
        id: 'ai-articles-tech',
        label: 'Technology Articles',
        description: 'Write about product technologies',
        path: '/about-business/sector?tab=research',
        icon: Newspaper,
        color: 'text-slate-500'
      },
      {
        id: 'aeo-category-pages',
        label: 'AEO Category Pages',
        description: 'Create enriched category landing pages',
        path: '/about-business/offering?tab=categories',
        icon: Layers,
        color: 'text-green-500'
      },
      {
        id: 'aeo-product-pages',
        label: 'AEO Product Pages',
        description: 'Create rich product display pages',
        path: '/about-business/offering?tab=products&view=aeo',
        icon: Package,
        color: 'text-purple-500'
      }
    ]
  },
  {
    id: 'quality-benchmark',
    name: 'Quality & Benchmark',
    description: 'Review content and compare against competitors',
    icon: CheckSquare,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    actions: [
      {
        id: 'competitive-benchmark',
        label: 'Competitive Benchmark',
        description: 'Compare your pages against the best',
        path: '/research/competitive-benchmark',
        icon: Trophy,
        color: 'text-amber-500'
      },
      {
        id: 'review-answers',
        label: 'Review Answers',
        description: 'Review AI-generated answers',
        path: '/answer-generation',
        icon: CheckSquare,
        color: 'text-green-500',
        comingSoon: true
      },
      {
        id: 'approve-qa',
        label: 'Approve Q&A',
        description: 'Approve questions and answers',
        path: '/qa-approval',
        icon: CheckSquare,
        color: 'text-blue-500',
        comingSoon: true
      }
    ]
  },
  {
    id: 'export-publish',
    name: 'Export & Publish',
    description: 'Get your work out to the world',
    icon: Download,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    actions: [
      {
        id: 'feed-export',
        label: 'Export Product Feeds',
        description: 'Download enriched product data',
        path: '/feed-export',
        icon: Download,
        color: 'text-indigo-500'
      }
    ]
  }
];

interface CapabilityToolkitsProps {
  defaultExpanded?: string[];
  highlightToolkit?: string;
}

export function CapabilityToolkits({ defaultExpanded = ['market-intelligence', 'content-creation'], highlightToolkit }: CapabilityToolkitsProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />
          Content Manager Toolkit
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          All your tools organized by purpose. Jump to any section based on what you need to do.
        </p>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" defaultValue={defaultExpanded} className="w-full">
          {toolkits.map((toolkit) => {
            const ToolkitIcon = toolkit.icon;
            const isHighlighted = highlightToolkit === toolkit.id;

            return (
              <AccordionItem
                key={toolkit.id}
                value={toolkit.id}
                className={isHighlighted ? 'ring-2 ring-primary ring-offset-2 rounded-lg' : ''}
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${toolkit.bgColor}`}>
                      <ToolkitIcon className={`h-4 w-4 ${toolkit.color}`} />
                    </div>
                    <div className="text-left">
                      <span className="font-semibold">{toolkit.name}</span>
                      <p className="text-xs text-muted-foreground font-normal">
                        {toolkit.description}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                    {toolkit.actions.map((action) => {
                      const ActionIcon = action.icon;

                      return (
                        <button
                          key={action.id}
                          onClick={() => !action.comingSoon && navigate(action.path)}
                          disabled={action.comingSoon}
                          className={`
                            flex items-start gap-3 p-3 rounded-lg border text-left
                            transition-all group
                            ${action.comingSoon
                              ? 'opacity-60 cursor-not-allowed bg-muted/30'
                              : 'hover:bg-accent hover:border-primary/30 hover:shadow-sm'
                            }
                          `}
                        >
                          <div className={`p-1.5 rounded-md bg-muted group-hover:bg-background transition-colors`}>
                            <ActionIcon className={`h-4 w-4 ${action.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{action.label}</span>
                              {action.isNew && (
                                <Badge variant="secondary" className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                                  New
                                </Badge>
                              )}
                              {action.comingSoon && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  Coming Soon
                                </Badge>
                              )}
                              {action.badge && (
                                <Badge variant="secondary" className={`${action.badgeColor || ''} text-[10px] px-1.5 py-0`}>
                                  {action.badge}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {action.description}
                            </p>
                          </div>
                          {!action.comingSoon && (
                            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}

export default CapabilityToolkits;
